/**
 * О проекте
 * ====================================
 */
import { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData, useLocation } from "react-router";
import invariant from "tiny-invariant";

import BlockQuote from "~/components/BlockQuote";
import Email from "~/components/Email";
import result from "~/components/tool-page/Result";
import { makeTitle } from "~/helpers/pageTitle";
import { createArticleSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import H2 from "~/components/H2";

const TKEY = "about";
const LAST_UPDATED = new Date("2024-02-01T00:00:00.000Z");


export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: makeTitle(data?.meta.title) },
  { name: "description", content: data?.meta.description },
  { "script:ld+json": schemaOrg.organization },
  createArticleSchema('en', 'About Us', [], LAST_UPDATED),
];

export const handle = {
  localisedLinks: ["en"]
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";

  const t = await getTranslator(request);

  return {
    result,
    meta: {
      locale,
      title: "About CountLike.PRO",
      description: t(`${TKEY}.description`),
    }
  };
};

export default function Index() {
  const {
    meta
  } = useLoaderData<typeof loader>();

  const locale = getLocale(useLocation().pathname);
  invariant(locale);



  return (
    <>
      <H2 className="mb-6">The Purpose</H2>
      <p><strong>Empowering smarter decisions through simple, free calculations.</strong></p>

      <p>In our daily lives, we're constantly faced with questions and choices that could benefit from clear, calculated
        answers. Whether it’s figuring out savings potential, planning time for a task, or calculating nutritional
        needs, we often skip the math and rely on guesswork.</p>

      <p>Our website provides an accessible set of calculators designed to help anyone make informed decisions, quickly
        and easily. By removing complexity, we allow you to focus on what truly matters—getting the answers you need in
        just a few clicks.</p>

      <p>A future where informed, data-driven choices shape our lives is one we aim to contribute to. With every
        calculation, we make the world just a bit more efficient and thoughtful.</p>

      <H2 className="mt-6 mb-4">The Team</H2>

      <div className="w-full flex flex-col justify-center items-center">
        <div className="max-w-[200px] flex flex-col justify-center items-center text-center">
          <div className="transition-all duration-300 ease-in-out  sm:py-5 hover:scale-110 hover:text-blue-400">
            <img className="rounded-circle" src="https://static.countlike.pro/Alex-Fedorov.webp" alt="Alex Fedorov" />

            <div
              className="text-center font-semibold mt-5 font-sans text-xl md:text-lg border-b border-dashed inline-block whitespace-nowrap">
              Alex Fedorov
            </div>
          </div>
          <div>Founder, author of most calculators on this website, IT Entrepreneur</div>
        </div>
      </div>


      <H2 className="mt-6 mb-4">Disclaimer</H2>
      <p>While aiming to achieve accuracy, there is an important disclaimer: </p>
      <BlockQuote className="my-4">
        Never trust any kind of online-calculators for critical staff, always double check result! As we are all humans,
        and humans make mistakes.
      </BlockQuote>
      <p>Other than that, please use it completely free!</p>
      <p>I you want to say thank you - please, place a link to any calculator on your website or social network. This
        helps a lot to rank in Google! </p>
      <p>Found an error? <Email email="we@countlike.pro">Drop us an email</Email></p>

      <H2 className="mt-6 mb-4">Privacy Policy</H2>
      <p className="text-sm">Last Updated: {LAST_UPDATED.toLocaleString('en')}</p>
      <p>This site does not collect or use any personal data. We only use <a href="https://analytics.google.com"
                                                                             target="_blank" rel="nofollow noreferrer">Google
        Analytics</a> and <a href="https://metrika.yandex.ru" target="_blank" rel="nofollow noreferrer">Yandex
        Metrika</a> to measure website traffic.</p>
    </>
  );
}


